/**
 * AI Agent 列表页 — 我的 Agent
 * 使用真实 API: GET /api/agents
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAgents, AgentDTO } from '../../api/agent';

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: '可用', color: '#34d399' },
  DRAFT: { label: '草稿', color: '#6b7280' },
  INACTIVE: { label: '停用', color: '#fb923c' },
  ARCHIVED: { label: '归档', color: '#9ca3af' },
};

const FILTERS = [
  { key: 'ALL', label: '全部' },
  { key: 'ACTIVE', label: '可用' },
  { key: 'DRAFT', label: '草稿' },
  { key: 'INACTIVE', label: '停用' },
  { key: 'ARCHIVED', label: '归档' },
];

export default function AgentListPage() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('ALL');

  const loadAgents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, unknown> = { page: 0, size: 50 };
      if (search) params.query = search;
      if (filter !== 'ALL') params.status = filter;
      const result = await fetchAgents(params);
      setAgents(result.items || []);
    } catch (err) {
      setError((err as Error).message);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [search, filter]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const filteredAgents = agents.filter((a) => {
    if (filter !== 'ALL' && a.status !== filter) return false;
    return true;
  });

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🤖 AI Agent</h1>
          <p style={styles.subtitle}>创建和管理你的智能体，让 AI 为你工作</p>
        </div>
        <button style={styles.createBtn} onClick={() => navigate('/simon-cat/agents/new')}>
          + 创建 Agent
        </button>
      </div>

      <div style={styles.toolbar}>
        <input
          style={styles.searchInput}
          placeholder="搜索 Agent 名称或描述..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={styles.filterTabs}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              style={{
                ...styles.filterTab,
                ...(filter === f.key ? styles.filterTabActive : {}),
              }}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={styles.loading}>加载中...</div>
      ) : error ? (
        <div style={styles.errorCard}>
          <p>⚠️ 加载失败: {error}</p>
          <button style={styles.retryBtn} onClick={loadAgents}>重试</button>
        </div>
      ) : filteredAgents.length === 0 ? (
        <div style={styles.empty}>
          <span style={styles.emptyEmoji}>🔍</span>
          <p>暂无 Agent</p>
          <button style={styles.emptyBtn} onClick={() => navigate('/simon-cat/agents/new')}>
            创建第一个 Agent
          </button>
        </div>
      ) : (
        <div style={styles.grid}>
          {filteredAgents.map((agent) => {
            const statusCfg = STATUS_MAP[agent.status || 'DRAFT'] || STATUS_MAP.DRAFT;
            return (
              <div
                key={agent.agentId}
                style={styles.card}
                onClick={() => navigate(`/simon-cat/agents/${agent.agentId}`)}
              >
                <div style={styles.cardTop}>
                  <span style={styles.cardAvatar}>{agent.avatar || '🤖'}</span>
                  <span style={{ ...styles.cardStatus, background: statusCfg.color }}>
                    {statusCfg.label}
                  </span>
                </div>
                <h3 style={styles.cardName}>{agent.name}</h3>
                <p style={styles.cardDesc}>{agent.description || '暂无描述'}</p>
                <div style={styles.cardFooter}>
                  <span style={styles.cardModel}>{agent.model || 'deepseek-chat'}</span>
                  <button
                    style={styles.runBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/simon-cat/agents/${agent.agentId}?action=run`);
                    }}
                  >
                    ⚡ 执行
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1000, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 12, color: '#64748b', margin: '4px 0 0' },
  createBtn: {
    padding: '8px 18px', borderRadius: 8, border: '1px solid #6366f1',
    background: 'rgba(99,102,241,0.12)', color: '#a5b4fc', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  toolbar: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 },
  searchInput: {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)',
    color: '#e2e8f0', fontSize: 13, outline: 'none',
  },
  filterTabs: { display: 'flex', gap: 6 },
  filterTab: {
    padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.06)',
    background: 'transparent', color: '#94a3b8', fontSize: 11, cursor: 'pointer',
  },
  filterTabActive: {
    background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc',
  },
  loading: { textAlign: 'center', padding: 40, color: '#64748b' },
  errorCard: {
    padding: 20, borderRadius: 10, background: 'rgba(248,113,113,0.05)',
    border: '1px solid rgba(248,113,113,0.2)', textAlign: 'center',
  },
  retryBtn: {
    marginTop: 10, padding: '6px 16px', borderRadius: 6,
    border: '1px solid #6366f1', background: 'rgba(99,102,241,0.12)',
    color: '#a5b4fc', fontSize: 12, cursor: 'pointer',
  },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 50 },
  emptyEmoji: { fontSize: 40 },
  emptyText: { color: '#64748b', fontSize: 13, margin: 0 },
  emptyBtn: {
    padding: '8px 18px', borderRadius: 8, border: '1px solid #6366f1',
    background: 'rgba(99,102,241,0.12)', color: '#a5b4fc', fontSize: 12, cursor: 'pointer',
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 },
  card: {
    padding: 16, borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.025)', cursor: 'pointer', transition: 'all 0.2s',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  cardAvatar: { fontSize: 28 },
  cardStatus: { fontSize: 10, padding: '2px 8px', borderRadius: 6, color: 'white', fontWeight: 500 },
  cardName: { fontSize: 14, fontWeight: 600, margin: '0 0 4px' },
  cardDesc: {
    fontSize: 11, color: '#94a3b8', margin: '0 0 10px',
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
  },
  cardFooter: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.04)',
  },
  cardModel: {
    fontSize: 10, fontFamily: 'monospace', color: '#64748b',
    padding: '2px 6px', borderRadius: 4, background: 'rgba(255,255,255,0.04)',
  },
  runBtn: {
    padding: '4px 10px', borderRadius: 6, border: '1px solid #6366f1',
    background: 'rgba(99,102,241,0.12)', color: '#a5b4fc', fontSize: 11, fontWeight: 600, cursor: 'pointer',
  },
};
